import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Copy,
  Check,
  Key,
  RefreshCw,
  ChevronDown,
  FileText,
  CheckSquare,
  ShoppingCart,
  Search,
  Users,
  Loader2,
} from "lucide-react";
import { cn } from "../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API Endpoints organized by category
const API_ENDPOINTS = {
  auth: {
    title: "Authentication",
    icon: Key,
    description: "Login, register, and manage authentication",
    endpoints: [
      { method: "POST", path: "/api/auth/register", desc: "Register a new user", body: '{"username": "john", "email": "john@example.com", "password": "secret123"}' },
      { method: "POST", path: "/api/auth/login", desc: "Login and get token", body: '{"email": "john@example.com", "password": "secret123"}' },
      { method: "GET", path: "/api/auth/me", desc: "Get current user info", auth: true },
    ],
  },
  notes: {
    title: "My Notes",
    icon: FileText,
    description: "Create, read, update, delete personal notes",
    endpoints: [
      { method: "GET", path: "/api/files", desc: "List all files/folders", auth: true },
      { method: "POST", path: "/api/files", desc: "Create file/folder", auth: true, body: '{"name": "my-note.md", "parent_path": "", "is_folder": false, "content": "# Hello"}' },
      { method: "GET", path: "/api/files/{file_path}", desc: "Get file content", auth: true },
      { method: "PUT", path: "/api/files/{file_path}", desc: "Update file content", auth: true, body: '{"content": "# Updated content"}' },
      { method: "DELETE", path: "/api/files/{file_path}", desc: "Delete file (move to trash)", auth: true },
      { method: "POST", path: "/api/files/rename", desc: "Rename file", auth: true, body: '{"old_path": "old.md", "new_name": "new.md"}' },
      { method: "POST", path: "/api/files/move", desc: "Move file", auth: true, body: '{"source_path": "file.md", "dest_path": "folder/file.md"}' },
      { method: "POST", path: "/api/files/pin", desc: "Toggle pin status", auth: true, body: '{"file_path": "note.md"}' },
    ],
  },
  commonNotes: {
    title: "Common Notes",
    icon: Users,
    description: "Shared notes visible to all users",
    endpoints: [
      { method: "GET", path: "/api/common/files", desc: "List all common files", auth: true },
      { method: "POST", path: "/api/common/files", desc: "Create common file", auth: true, body: '{"name": "shared.md", "parent_path": "", "is_folder": false, "content": "# Shared"}' },
      { method: "GET", path: "/api/common/files/{file_path}", desc: "Get common file content", auth: true },
      { method: "PUT", path: "/api/common/files/{file_path}", desc: "Update common file", auth: true, body: '{"content": "# Updated"}' },
      { method: "DELETE", path: "/api/common/files/{file_path}", desc: "Delete common file", auth: true },
      { method: "POST", path: "/api/common/files/rename", desc: "Rename common file", auth: true, body: '{"old_path": "old.md", "new_name": "new.md"}' },
      { method: "POST", path: "/api/common/files/move", desc: "Move common file", auth: true, body: '{"source_path": "file.md", "dest_path": "folder/file.md"}' },
      { method: "POST", path: "/api/common/files/pin", desc: "Toggle common file pin", auth: true, body: '{"file_path": "note.md"}' },
    ],
  },
  todos: {
    title: "My To-Dos",
    icon: CheckSquare,
    description: "Personal task management",
    endpoints: [
      { method: "GET", path: "/api/todos", desc: "List all todos (grouped by category)", auth: true },
      { method: "GET", path: "/api/todos?list_id={id}", desc: "List todos in specific list", auth: true },
      { method: "GET", path: "/api/todos/history", desc: "Get completed/deleted todos", auth: true },
      { method: "POST", path: "/api/todos", desc: "Create todo", auth: true, body: '{"text": "Buy milk", "category": "important", "list_id": null}' },
      { method: "PUT", path: "/api/todos/{todo_id}", desc: "Update todo", auth: true, body: '{"text": "Buy eggs", "category": "normal"}' },
      { method: "POST", path: "/api/todos/{todo_id}/complete", desc: "Mark as complete", auth: true },
      { method: "POST", path: "/api/todos/{todo_id}/uncomplete", desc: "Mark as incomplete", auth: true },
      { method: "DELETE", path: "/api/todos/{todo_id}", desc: "Delete todo", auth: true },
      { method: "POST", path: "/api/todos/reorder", desc: "Reorder todos", auth: true, body: '{"category": "important", "todo_ids": ["id1", "id2"]}' },
    ],
  },
  taskLists: {
    title: "Task Lists",
    icon: CheckSquare,
    description: "Organize todos into lists",
    endpoints: [
      { method: "GET", path: "/api/task-lists", desc: "List all task lists", auth: true },
      { method: "POST", path: "/api/task-lists", desc: "Create task list", auth: true, body: '{"name": "Shopping"}' },
      { method: "PUT", path: "/api/task-lists/{list_id}", desc: "Update task list", auth: true, body: '{"name": "Groceries"}' },
      { method: "DELETE", path: "/api/task-lists/{list_id}", desc: "Delete task list", auth: true },
    ],
  },
  commonTodos: {
    title: "Common To-Dos",
    icon: Users,
    description: "Shared tasks visible to all users",
    endpoints: [
      { method: "GET", path: "/api/common/todos", desc: "List all common todos", auth: true },
      { method: "GET", path: "/api/common/todos?list_id={id}", desc: "List common todos in list", auth: true },
      { method: "GET", path: "/api/common/todos/history", desc: "Get common todo history", auth: true },
      { method: "POST", path: "/api/common/todos", desc: "Create common todo", auth: true, body: '{"text": "Team meeting", "category": "important", "list_id": null}' },
      { method: "PUT", path: "/api/common/todos/{todo_id}", desc: "Update common todo", auth: true, body: '{"text": "Updated task"}' },
      { method: "POST", path: "/api/common/todos/{todo_id}/complete", desc: "Complete common todo", auth: true },
      { method: "POST", path: "/api/common/todos/{todo_id}/uncomplete", desc: "Uncomplete common todo", auth: true },
      { method: "DELETE", path: "/api/common/todos/{todo_id}", desc: "Delete common todo", auth: true },
    ],
  },
  commonTaskLists: {
    title: "Common Task Lists",
    icon: Users,
    description: "Shared task lists",
    endpoints: [
      { method: "GET", path: "/api/common/task-lists", desc: "List common task lists", auth: true },
      { method: "POST", path: "/api/common/task-lists", desc: "Create common task list", auth: true, body: '{"name": "Team Tasks"}' },
      { method: "PUT", path: "/api/common/task-lists/{list_id}", desc: "Update common task list", auth: true, body: '{"name": "Updated Name"}' },
      { method: "DELETE", path: "/api/common/task-lists/{list_id}", desc: "Delete common task list", auth: true },
    ],
  },
  orders: {
    title: "Orders",
    icon: ShoppingCart,
    description: "Order management (global)",
    endpoints: [
      { method: "GET", path: "/api/orders", desc: "List all orders", auth: true },
      { method: "GET", path: "/api/orders/history", desc: "Get archived orders", auth: true },
      { method: "GET", path: "/api/orders/statuses", desc: "Get order statuses", auth: true },
      { method: "GET", path: "/api/orders/{order_id}", desc: "Get single order", auth: true },
      { method: "POST", path: "/api/orders", desc: "Create order", auth: true, body: '{"name": "John Doe", "phone": "+1234567890", "email": "john@example.com", "address": "123 Main St", "description": "Order details", "status": "New", "pipeline": "active"}' },
      { method: "PUT", path: "/api/orders/{order_id}", desc: "Update order", auth: true, body: '{"name": "Jane Doe", "status": "Processing", "pipeline": "offers"}' },
      { method: "POST", path: "/api/orders/{order_id}/archive", desc: "Archive order", auth: true },
      { method: "DELETE", path: "/api/orders/{order_id}", desc: "Delete order", auth: true },
    ],
  },
  search: {
    title: "Search",
    icon: Search,
    description: "Search across notes, todos, and orders",
    endpoints: [
      { method: "POST", path: "/api/search", desc: "Search in notes", auth: true, body: '{"query": "hello"}' },
      { method: "POST", path: "/api/search/advanced", desc: "Advanced search", auth: true, body: '{"query": "hello", "scope": "all"}' },
    ],
  },
  trash: {
    title: "Trash",
    icon: FileText,
    description: "Manage deleted items",
    endpoints: [
      { method: "GET", path: "/api/trash", desc: "List trashed items", auth: true },
      { method: "POST", path: "/api/trash/restore/{item_id}", desc: "Restore item from trash", auth: true },
      { method: "DELETE", path: "/api/trash/{item_id}", desc: "Permanently delete item", auth: true },
      { method: "DELETE", path: "/api/trash", desc: "Empty trash", auth: true },
    ],
  },
};

const MethodBadge = ({ method }) => {
  const colors = {
    GET: "bg-green-500/10 text-green-600 border-green-500/20",
    POST: "bg-blue-500/10 text-blue-600 border-blue-500/20",
    PUT: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
    DELETE: "bg-red-500/10 text-red-600 border-red-500/20",
  };
  return (
    <Badge variant="outline" className={cn("font-mono text-xs", colors[method])}>
      {method}
    </Badge>
  );
};

const EndpointCard = ({ endpoint, baseUrl }) => {
  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const curlCommand = `curl -X ${endpoint.method} "${baseUrl}${endpoint.path}" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"${endpoint.body ? ` \\
  -d '${endpoint.body}'` : ""}`;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center gap-3 p-3 hover:bg-muted/50 rounded-lg cursor-pointer border border-transparent hover:border-border transition-colors">
          <MethodBadge method={endpoint.method} />
          <code className="text-sm flex-1 font-mono text-muted-foreground">{endpoint.path}</code>
          <span className="text-sm text-muted-foreground hidden sm:inline">{endpoint.desc}</span>
          {endpoint.auth && <Badge variant="secondary" className="text-xs">Auth</Badge>}
          <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-4 p-4 bg-muted/30 rounded-lg mt-2 space-y-3">
          <p className="text-sm">{endpoint.desc}</p>
          
          {endpoint.body && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Request Body:</p>
              <pre className="text-xs bg-background p-2 rounded overflow-x-auto">
                {JSON.stringify(JSON.parse(endpoint.body), null, 2)}
              </pre>
            </div>
          )}
          
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs text-muted-foreground">cURL Example:</p>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2"
                onClick={() => copyToClipboard(curlCommand)}
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              </Button>
            </div>
            <pre className="text-xs bg-background p-2 rounded overflow-x-auto whitespace-pre-wrap">
              {curlCommand}
            </pre>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default function ApiDocsPage() {
  const [apiKey, setApiKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchApiKey();
  }, []);

  const fetchApiKey = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/api-key`);
      setApiKey(response.data.api_key || "");
    } catch (error) {
      console.error("Failed to fetch API key:", error);
    }
  };

  const regenerateApiKey = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/user/api-key/regenerate`);
      setApiKey(response.data.api_key);
      toast.success("API key regenerated");
    } catch (error) {
      toast.error("Failed to regenerate API key");
    } finally {
      setIsLoading(false);
    }
  };

  const copyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    toast.success("API key copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="h-full flex flex-col" data-testid="api-docs-page">
      {/* Header */}
      <div className="border-b border-border px-4 md:px-6 py-4">
        <div className="flex items-center gap-3">
          <Key className="w-5 h-5 text-primary" />
          <h1 className="text-xl font-semibold">API Documentation</h1>
          <Badge variant="secondary">v1.0</Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Use these endpoints to integrate NoteKeeper with external applications
        </p>
      </div>

      <ScrollArea className="flex-1">
        <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
          {/* API Key Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Key className="w-5 h-5" />
                Your API Key
              </CardTitle>
              <CardDescription>
                Use this key in the Authorization header: <code className="bg-muted px-1 rounded">Bearer YOUR_API_KEY</code>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 relative">
                  <Input
                    type="text"
                    value={apiKey || "No API key generated"}
                    readOnly
                    className="font-mono text-sm pr-10"
                  />
                  {apiKey && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                      onClick={copyApiKey}
                    >
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  )}
                </div>
                <Button onClick={regenerateApiKey} disabled={isLoading} variant="outline">
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  {apiKey ? "Regenerate" : "Generate"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Base URL: <code className="bg-muted px-1 rounded">{API_URL}</code>
              </p>
            </CardContent>
          </Card>

          {/* Quick Start */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Start</CardTitle>
              <CardDescription>Example: Create a new note</CardDescription>
            </CardHeader>
            <CardContent>
              <pre className="text-xs bg-muted p-3 rounded overflow-x-auto">
{`curl -X POST "${API_URL}/api/files" \\
  -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "my-note.md", "content": "# Hello World"}'`}
              </pre>
            </CardContent>
          </Card>

          {/* Endpoints by Category */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">API Endpoints</h2>
            
            <Tabs defaultValue="notes" className="w-full">
              <TabsList className="flex-wrap h-auto gap-1 p-1">
                {Object.entries(API_ENDPOINTS).map(([key, category]) => {
                  const Icon = category.icon;
                  return (
                    <TabsTrigger key={key} value={key} className="text-xs px-2 py-1.5">
                      <Icon className="w-3 h-3 mr-1" />
                      <span className="hidden sm:inline">{category.title}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              
              {Object.entries(API_ENDPOINTS).map(([key, category]) => (
                <TabsContent key={key} value={key} className="mt-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <category.icon className="w-4 h-4" />
                        {category.title}
                      </CardTitle>
                      <CardDescription>{category.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {category.endpoints.map((endpoint, i) => (
                        <EndpointCard key={i} endpoint={endpoint} baseUrl={API_URL} />
                      ))}
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          </div>

          {/* Response Format */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Response Format</CardTitle>
              <CardDescription>All responses are in JSON format</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium mb-2">Success Response:</p>
                <pre className="text-xs bg-muted p-3 rounded overflow-x-auto">
{`{
  "id": "abc123",
  "name": "my-note.md",
  "content": "# Hello World",
  "created_at": "2024-01-01T00:00:00Z"
}`}
                </pre>
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Error Response:</p>
                <pre className="text-xs bg-muted p-3 rounded overflow-x-auto">
{`{
  "detail": "Error message here"
}`}
                </pre>
              </div>
            </CardContent>
          </Card>

          {/* Categories Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Todo Categories</CardTitle>
              <CardDescription>Available categories for tasks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Badge variant="destructive">important</Badge>
                <Badge variant="secondary">normal</Badge>
                <Badge className="bg-yellow-500">waiting</Badge>
                <Badge variant="outline">paused</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Pipeline Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Order Pipelines</CardTitle>
              <CardDescription>Available pipelines for orders</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Badge className="bg-blue-500">active</Badge>
                <Badge className="bg-yellow-500">offers</Badge>
                <Badge className="bg-purple-500">to_deliver</Badge>
                <Badge className="bg-green-500">to_pickup</Badge>
                <Badge variant="secondary">history</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </ScrollArea>
    </div>
  );
}
